/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent } from '@msdyn365-commerce/core';
import * as React from 'react';

import {
    ICheckoutDeliveryOption,
    ICheckoutDeliveryOptionEdit,
    ICheckoutDeliveryOptionsList,
    ICheckoutDeliveryOptionsViewProps
} from './new-checkout-delivery-options';

const DeliveryOptionList: React.FC<ICheckoutDeliveryOptionsList> = ({ DeliveryOptionsList, list }) => {
    if (!list || list.length === 0) {
        return null;
    }

    return (
        <Node {...DeliveryOptionsList}>
            {list.map((deliveryOption: ICheckoutDeliveryOptionEdit) => {
                return (
                    <Node {...deliveryOption.DeliveryOption} key={deliveryOption.code} aria-label='Please select a delivery option.'>
                        {deliveryOption.radioButton}
                        {deliveryOption.description}
                        {deliveryOption.price}
                    </Node>
                );
            })}
        </Node>
    );
};

const DeliveryOptionSelected: React.FC<ICheckoutDeliveryOption> = ({ DeliveryOption, description, price }) => {
    return (
        <Node {...DeliveryOption}>
            {description}
            {price}
        </Node>
    );
};

const CheckoutDeliveryOptionsView: React.FC<ICheckoutDeliveryOptionsViewProps> = props => {
    const {
        CheckoutDeliveryOptions,
        enableCheckoutCartLineDeliveryOptions,
        checkoutErrorRef,
        viewState,
        deliveryOptions,
        deliveryOptionSelected,
        alert,
        waiting,
        saveButton,
        editButton,
        cancelButton,
        checkoutCartLineGroupDeliveryOptions,
        cartLineImages,
        config
    } = props;

    // Render introductory text if configured
    const extraTextField = config.paragraph;
    const extraText = extraTextField && <RichTextComponent text={extraTextField} className='ms-checkout__extratext' />;

    const filterElements = React.useCallback((elements: React.ReactNode): React.ReactNode => {
        return React.Children.toArray(elements)
            .filter(
                child =>
                    React.isValidElement(child) &&
                    !child.props.className?.includes('ms-checkout-delivery-options__cart-line-grp__item__ctn-lines')
            )
            .map(child => {
                if (React.isValidElement(child) && child.props.children) {
                    return React.cloneElement(child as React.ReactElement, {
                        children: filterElements(child.props.children)
                    });
                }
                return child;
            });
    }, []);

    const filteredCheckoutCartLineGroupDeliveryOptions = props.config.enableMultipleDeliveryOptions
        ? checkoutCartLineGroupDeliveryOptions
        : filterElements(checkoutCartLineGroupDeliveryOptions);

    return (
        // TODO: All wrapper div should be provided by viewProps. Once SDK provide the sln, update markup.
        <Module {...CheckoutDeliveryOptions} ref={checkoutErrorRef}>
            {extraText}
            {viewState.isLoading && waiting}
            {viewState.isError && alert}
            {viewState.isShowList && deliveryOptions && !enableCheckoutCartLineDeliveryOptions && (
                <DeliveryOptionList {...deliveryOptions} />
            )}
            {viewState.isShowList && enableCheckoutCartLineDeliveryOptions && filteredCheckoutCartLineGroupDeliveryOptions}
            {viewState.isShowSelected && enableCheckoutCartLineDeliveryOptions && cartLineImages}
            {viewState.isShowSelected && deliveryOptionSelected && <DeliveryOptionSelected {...deliveryOptionSelected} />}
            {viewState.isShowSaveButton && saveButton}
            {viewState.isShowEditButton && editButton}
            {viewState.isShowCancelButton && cancelButton}
        </Module>
    );
};

export default CheckoutDeliveryOptionsView;
